/* eslint-disable react/jsx-props-no-spreading */

import React from 'react';
import classNames from 'classnames';
import { Accept, useDropzone } from 'react-dropzone';

import Button, { ButtonType } from 'ui/Button';

import { MediaUploaderItemType, MediaUploaderItemValue } from 'ui/MediaUploader';
import styles from './index.module.scss';

interface VideoMediafilePreviewItemContentProps {
  title?: string | null;
  duration?: number | null;
  imgLink?: string | null;
  uploading?: boolean;
  actionText?: string;
  actionHandler?: () => void;
  extension?: string | null;
  error?: string | Error | null;
  url?: string;
}

export type MediaUploaderProps = {
  className?: string;
  multiple?: boolean;
  accept: Accept;
  items: MediaUploaderItemType[];
  isLoading: boolean;
  elementId: string;
  buttonTitle: string;
  description: string;
  isMediaUploaderVisible?: boolean;
  onDrop: (files: File[]) => void;
  onRemove: (value: MediaUploaderItemValue) => void;
  onRetry: (value: MediaUploaderItemValue) => void;
  renderer: React.ElementType<VideoMediafilePreviewItemContentProps>;
  postfix?: React.ElementType;
};

const VideoMediaUploader = (props: MediaUploaderProps) => {
  const {
    className,
    multiple = true,
    accept,
    items,
    isLoading,
    elementId,
    buttonTitle,
    description,
    isMediaUploaderVisible = true,
    onDrop,
    onRemove,
    onRetry,
    renderer: MediaListRenderer,
    postfix: MediaListRendererChildren,
  } = props;

  const dropzone = useDropzone({
    multiple,
    accept,
    onDrop,
  });

  const onClickUpload = () => {
    dropzone.open();
  };

  return (
    <div
      className={classNames(styles.MediaUploader, className)}
      id={elementId}>
      {isMediaUploaderVisible && (
        <>
          <Button
            elementId={`${elementId}_button`}
            className={styles.button}
            isLoading={isLoading}
            type={ButtonType.SECONDARY}
            onClick={onClickUpload}>
            {buttonTitle}
          </Button>

          <div {...dropzone.getRootProps({ style: { display: 'none' } })}>
            <input
              data-testid={`${elementId}_input`}
              {...dropzone.getInputProps()}
            />
          </div>

          <span className={styles.desc}>{description}</span>
        </>
      )}

      <ul
        id={`${elementId}_list`}
        data-testid={`${elementId}_list`}
        className={styles.list}>
        {items.map((item) => {
          const isRemoveVisible = !item.error && !isLoading && onRemove;

          const removeHandler = () => onRemove(item.value);
          const retryHandler = () => onRetry(item.value);

          return (
            <div className={styles.line}>
              <MediaListRenderer
                key={`media_upload_item-${item.value.id}`}
                title={item.label}
                uploading={item.isLoading}
                extension={item.value?.extension}
                imgLink={item.value?.previewUrl || item.value?.imgLink}
                duration={item.value.duration}
                error={item.error}
                url={item.value?.url}
              />
              {MediaListRendererChildren && (
                <div className={styles.children}>
                  <MediaListRendererChildren
                    id={elementId}
                    isRemove={isRemoveVisible}
                    isError={item.error}
                    onRemove={removeHandler}
                    onRetry={retryHandler}
                    isMultiple
                  />
                </div>
              )}
            </div>
          );
        })}
      </ul>
    </div>
  );
};

export default VideoMediaUploader;
